import { useEffect, useRef } from "react";

import { useAccountSelector, useGlobalSocket, useUserIdSelector } from "store";
import { IBulkBatchEvent, IBulkFileStatusEvent } from "store/types";

/**
 * Subscribes to the account-global bulk-extraction channel and forwards the
 * three realtime events. Modeled on use-extraction-result-socket-hook.
 *
 * NOTE on event names: the backend defines a custom Laravel broadcastAs name,
 * which arrives over the wire WITH a leading dot. The app's other socket hooks
 * bind dot-less names because those events don't use broadcastAs. To be robust
 * against both, we bind each event under both the dotted and dot-less name —
 * only the name the backend actually emits will ever fire.
 */
const FILE_STATUS = "bulk-extraction.file-status";
const AWAITING_REVIEW = "bulk-extraction.batch-awaiting-review";
const DISMISSED = "bulk-extraction.batch-dismissed";

interface Handlers {
  onFileStatus: (event: IBulkFileStatusEvent) => void;
  onBatchAwaitingReview: (event: IBulkBatchEvent) => void;
  onBatchDismissed: (event: IBulkBatchEvent) => void;
}

export function useBulkExtractionSocketHook(handlers: Handlers): void {
  const socket = useGlobalSocket();
  const userId = useUserIdSelector();
  const { enable_bulk_upload_review } = useAccountSelector() || {};

  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!socket || !userId || !enable_bulk_upload_review) return;

    const channelName = `private-bulk-extraction.${userId}`;
    const channel = socket.subscribe(channelName);

    const bindBoth = (event: string, cb: (data: IBulkFileStatusEvent | IBulkBatchEvent) => void) => {
      channel.bind(event, cb);
      channel.bind(`.${event}`, cb);
    };

    bindBoth(FILE_STATUS, (data) => handlersRef.current.onFileStatus(data as IBulkFileStatusEvent));
    bindBoth(AWAITING_REVIEW, (data) => handlersRef.current.onBatchAwaitingReview(data as IBulkBatchEvent));
    bindBoth(DISMISSED, (data) => handlersRef.current.onBatchDismissed(data as IBulkBatchEvent));

    return () => {
      socket.unsubscribe(channelName);
    };
  }, [socket, userId, enable_bulk_upload_review]);
}
